import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    HStack,
    Icon,
    Skeleton,
    SkeletonText,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useBreakpointValue,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import {
    FiAlertCircle,
    FiCalendar,
    FiClock,
    FiMapPin,
    FiMoreHorizontal,
    FiRefreshCw,
    FiSearch,
    FiUser,
} from 'react-icons/fi';
import { AccessRecord } from '../hooks/useAccessList';

interface AccessListProps {
  acessos: AccessRecord[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  onLoadMore: () => void;
  onRefresh: () => void;
}

const AccessList: React.FC<AccessListProps> = ({
  acessos,
  loading,
  loadingMore,
  hasMore,
  error,
  onLoadMore,
  onRefresh,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const formatDateTime = (date: string, time: string) => {
    if (!date) return '-';
    try {
      const formattedDate = new Date(date).toLocaleDateString('pt-BR');
      return time ? `${formattedDate} ${time}` : formattedDate;
    } catch {
      return `${date} ${time || ''}`.trim();
    }
  };

  const calculateDuration = (tempoPermancencia: string | null) => {
    if (!tempoPermancencia) return '-';
    return tempoPermancencia;
  };

  // Loading inicial
  if (loading && acessos.length === 0) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Box key={index} w="100%" p={4} bg={bgColor} borderRadius="md">
                <HStack spacing={4}>
                  <Skeleton height="10px" width="200px" />
                  <Skeleton height="10px" width="150px" />
                  <Skeleton height="10px" width="100px" />
                </HStack>
                <SkeletonText mt={2} noOfLines={2} spacing={2} />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    );
  }

  // Estado de erro
  if (error && acessos.length === 0) {
    return (
      <Card>
        <CardBody>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Erro ao carregar acessos!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="blue"
            onClick={onRefresh}
            mt={4}
          >
            Tentar Novamente
          </Button>
        </CardBody>
      </Card>
    );
  }

  // Estado vazio
  if (!loading && acessos.length === 0) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} py={8}>
            <Icon as={FiAlertCircle} boxSize={16} color="gray.400" />
            <Text fontSize="lg" color="gray.600" textAlign="center">
              Nenhum acesso encontrado
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Tente ajustar os filtros ou verifique novamente mais tarde
            </Text>
            <Button leftIcon={<FiRefreshCw />} onClick={onRefresh} variant="outline">
              Atualizar
            </Button>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  // Renderização mobile (cards)
  if (isMobile) {
    return (
      <VStack spacing={4}>
        {acessos.map((acesso) => (
          <Card key={acesso.id} w="100%">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack justify="space-between" w="100%">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="blue.600">
                      {acesso.usuarioNome}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {acesso.usuarioDocumento}
                    </Text>
                  </VStack>
                  <Badge
                    colorScheme={acesso.dataSaida ? 'green' : 'blue'}
                    variant="solid"
                  >
                    {acesso.dataSaida ? 'Saiu' : 'Presente'}
                  </Badge>
                </HStack>

                <VStack align="start" spacing={2} w="100%">
                  <HStack>
                    <Icon as={FiMapPin} color="gray.500" />
                    <Text fontSize="sm">
                      {acesso.loja} - {acesso.sala}
                      {acesso.palestra ? ` · ${acesso.palestra}` : ''}
                    </Text>
                  </HStack>
                  
                  <HStack>
                    <Icon as={FiCalendar} color="gray.500" />
                    <Text fontSize="sm">
                      Entrada: {formatDateTime(acesso.dataEntrada, acesso.horarioEntrada)}
                    </Text>
                  </HStack>

                  {acesso.dataSaida && (
                    <HStack>
                      <Icon as={FiClock} color="gray.500" />
                      <Text fontSize="sm">
                        Saída: {formatDateTime(acesso.dataSaida, acesso.horarioSaida || '')}
                      </Text>
                    </HStack>
                  )}

                  <HStack>
                    <Icon as={FiSearch} color="purple.500" />
                    <Text fontSize="sm" fontWeight="medium" color="purple.600">
                      Método: {acesso.entrySearchMethod}
                    </Text>
                  </HStack>

                  <HStack>
                    <Icon as={FiUser} color="gray.500" />
                    <Text fontSize="sm">Operador: {acesso.operador}</Text>
                  </HStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        ))}

        {/* Botão Carregar Mais - Mobile */}
        <Box w="100%" textAlign="center" py={4}>
          {error && acessos.length > 0 && (
            <Alert status="error" borderRadius="lg" mb={4}>
              <AlertIcon />
              <Text>{error}</Text>
            </Alert>
          )}
          
          <Button
            onClick={onLoadMore}
            disabled={!hasMore || loadingMore}
            size="lg"
            colorScheme="blue"
            variant={hasMore ? 'solid' : 'outline'}
            w="100%"
            leftIcon={loadingMore ? <Spinner size="sm" /> : <FiMoreHorizontal />}
          >
            {loadingMore
              ? 'Carregando...'
              : hasMore
              ? `Carregar Mais`
              : 'Todos os registros carregados'}
          </Button>
        </Box>
      </VStack>
    );
  }

  // Renderização desktop (tabela)
  return (
    <Card>
      <CardBody p={0}>
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Nome do Usuário</Th>
                <Th>Documento</Th>
                <Th>Email</Th>
                <Th>Loja/Sala</Th>
                <Th>Palestra</Th>
                <Th>Entrada</Th>
                <Th>Saída</Th>
                <Th>Tempo</Th>
                <Th>Método de Busca</Th>
                <Th>Operador</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {acessos.map((acesso) => (
                <Tr key={acesso.id} _hover={{ bg: tableHoverBg }}>
                  <Td>
                    <Text fontWeight="medium" color="blue.600">
                      {acesso.usuarioNome}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{acesso.usuarioDocumento}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{acesso.usuarioEmail}</Text>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {acesso.loja}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {acesso.sala}
                      </Text>
                    </VStack>
                  </Td>
                  {/*
                    Uma sala recebe varias palestras no dia; a coluna diz em qual delas a
                    pessoa entrou. Vazia quando nao havia sessao cadastrada no horario —
                    intervalo, montagem, ou cronograma incompleto.
                  */}
                  <Td maxW="260px">
                    {acesso.palestra ? (
                      <Text fontSize="sm" noOfLines={2} title={acesso.palestra}>
                        {acesso.palestra}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        fora de palestra
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {formatDateTime(acesso.dataEntrada, acesso.horarioEntrada)}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {acesso.dataSaida
                        ? formatDateTime(acesso.dataSaida, acesso.horarioSaida || '')
                        : '-'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{calculateDuration(acesso.tempoPermancencia)}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="medium" color="purple.600">
                      {acesso.entrySearchMethod}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{acesso.operador}</Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={acesso.dataSaida ? 'green' : 'blue'}
                      variant="solid"
                    >
                      {acesso.dataSaida ? 'Saiu' : 'Presente'}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Botão Carregar Mais - Desktop */}
        <Box textAlign="center" p={6} borderTop="1px solid" borderColor={borderColor}>
          {error && acessos.length > 0 && (
            <Alert status="error" borderRadius="lg" mb={4}>
              <AlertIcon />
              <Text>{error}</Text>
            </Alert>
          )}
          
          <Button
            onClick={onLoadMore}
            disabled={!hasMore || loadingMore}
            size="lg"
            colorScheme="blue"
            variant={hasMore ? 'solid' : 'outline'}
            leftIcon={loadingMore ? <Spinner size="sm" /> : <FiMoreHorizontal />}
          >
            {loadingMore
              ? 'Carregando...'
              : hasMore
              ? `Carregar Mais`
              : 'Todos os registros carregados'}
          </Button>
        </Box>
      </CardBody>
    </Card>
  );
};

export default AccessList; 